import { collection, deleteDoc, doc, getDoc, setDoc, Timestamp, updateDoc, } from "firebase/firestore";
import { warnUnknownFields } from "./converterWarnings";
import { prepareForFirestore } from "./utils";
const OUTBOUND_PLAYBOOK_FIELDS = [
    "id",
    "path",
    "companyId",
    "name",
    "description",
    "greeting",
    "status",
    "version",
    "conversationInstructions",
    "typicalFlows",
    "inputSchema",
    "resultSchema",
    "timezone",
    "businessHours",
    "initialDelaySeconds",
    "retryPolicy",
    "terminalOutcomes",
    "callConfiguration",
    "createdAt",
    "updatedAt",
    "enabledAt",
    "disabledAt",
    "archivedAt",
    "createdBy",
    "updatedBy",
];
function toDate(value) {
    if (value instanceof Date)
        return value;
    if (value instanceof Timestamp)
        return value.toDate();
    if (typeof value === "string") {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
}
export const outboundPlaybookConverter = {
    toFirestore(value) {
        return prepareForFirestore(value);
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui OutboundPlaybook.fromFirestore", data, OUTBOUND_PLAYBOOK_FIELDS);
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId || "",
            name: data.name || "",
            description: data.description || null,
            greeting: data.greeting || null,
            status: (data.status || "draft"),
            version: Number(data.version || 1),
            conversationInstructions: data.conversationInstructions || "",
            typicalFlows: Array.isArray(data.typicalFlows) ? data.typicalFlows : [],
            inputSchema: data.inputSchema || {},
            resultSchema: data.resultSchema || {},
            timezone: data.timezone || "America/New_York",
            businessHours: Array.isArray(data.businessHours)
                ? data.businessHours
                : [],
            initialDelaySeconds: Number(data.initialDelaySeconds || 0),
            retryPolicy: {
                maxAttempts: Number(data.retryPolicy?.maxAttempts || 1),
                backoffSeconds: Array.isArray(data.retryPolicy?.backoffSeconds)
                    ? data.retryPolicy.backoffSeconds
                    : [],
            },
            terminalOutcomes: Array.isArray(data.terminalOutcomes)
                ? data.terminalOutcomes
                : [],
            callConfiguration: {
                agentId: data.callConfiguration?.agentId || null,
                serviceId: data.callConfiguration?.serviceId || null,
                fromPhoneNumber: data.callConfiguration?.fromPhoneNumber || null,
                workerType: "outbound_call",
            },
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            enabledAt: toDate(data.enabledAt),
            disabledAt: toDate(data.disabledAt),
            archivedAt: toDate(data.archivedAt),
            createdBy: data.createdBy || null,
            updatedBy: data.updatedBy || null,
        };
    },
};
export function outboundPlaybooksCollection(db, companyId) {
    return collection(db, "companies", companyId, "outboundPlaybooks").withConverter(outboundPlaybookConverter);
}
export function outboundPlaybookDocument(db, companyId, playbookId) {
    return doc(db, "companies", companyId, "outboundPlaybooks", playbookId).withConverter(outboundPlaybookConverter);
}
export async function fetchOutboundPlaybook(db, companyId, playbookId) {
    const snapshot = await getDoc(outboundPlaybookDocument(db, companyId, playbookId));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function createOutboundPlaybook(db, data) {
    const docRef = doc(outboundPlaybooksCollection(db, data.companyId));
    const now = new Date();
    await setDoc(docRef, {
        ...data,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
    });
    return docRef.id;
}
export async function updateOutboundPlaybook(db, companyId, playbookId, data) {
    await updateDoc(outboundPlaybookDocument(db, companyId, playbookId), {
        ...data,
        updatedAt: new Date(),
    });
}
export async function deleteOutboundPlaybook(db, companyId, playbookId) {
    await deleteDoc(outboundPlaybookDocument(db, companyId, playbookId));
}
